import React, { useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { usePEInvoiceStore } from '../../store/usePEInvoiceStore';
import { generateEpcQrPayload } from '../../utils/pe-papers/epcGiroCodeGenerator';
import {
  ESTIMATE_PROGNOSIS_DISCLAIMER,
  formatInvoiceItemServiceMeta,
} from '../../utils/pe-papers/peInvoiceItemMeta';
import { getPeInvoiceSenderFromAppStore } from '../../utils/pe-papers/peInvoiceSenderFromAppStore';

const formatEuro = (value: number) =>
  value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const formatDateDe = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE');
};

const addDaysIso = (iso: string, days: number) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + (days || 0));
  return d.toLocaleDateString('de-DE');
};

const lineAmounts = (
  quantity: number,
  unitPrice: number,
  priceMode: 'gross' | 'net',
  vatRate: number,
  isVatExempt: boolean
) => {
  const line = (quantity || 0) * (unitPrice || 0);
  const rate = isVatExempt ? 0 : vatRate;
  if (rate === 0) {
    return { net: line, gross: line, unitNet: unitPrice, unitGross: unitPrice };
  }
  if (priceMode === 'gross') {
    const net = line / (1 + rate / 100);
    const unitNet = unitPrice / (1 + rate / 100);
    return { net, gross: line, unitNet, unitGross: unitPrice };
  }
  const gross = line * (1 + rate / 100);
  const unitGross = unitPrice * (1 + rate / 100);
  return { net: line, gross, unitNet: unitPrice, unitGross };
};

const PEInvoicePreview: React.FC = () => {
  const profile = usePEInvoiceStore((s) => s.profile);
  const customer = usePEInvoiceStore((s) => s.customer);
  const items = usePEInvoiceStore((s) => s.items);
  const shipping = usePEInvoiceStore((s) => s.shipping);
  const meta = usePEInvoiceStore((s) => s.meta);
  const getCalculations = usePEInvoiceStore((s) => s.getCalculations);

  const calc = getCalculations();
  const isVatExempt = calc.isVatExempt;
  const standardVatRate = calc.standardVatRate;
  const isEstimate = meta.docType === 'estimate';
  const dueDate = addDaysIso(meta.invoiceDate, meta.paymentDeadlineDays);
  const sender = getPeInvoiceSenderFromAppStore();
  const senderName = sender.displayName || profile.accountHolder || '—';
  const senderStreet = sender.street;
  const senderCityLine = [sender.zip, sender.city].filter(Boolean).join(' ');
  const kontoinhaber = profile.accountHolder || sender.accountHolder || senderName;
  const showShipping = shipping.enabled && shipping.amount > 0;
  const displayVatRate = isVatExempt ? 0 : standardVatRate;

  const qrPayload = useMemo(
    () =>
      isEstimate
        ? ''
        : generateEpcQrPayload({
            bic: profile.bic,
            accountHolder: kontoinhaber,
            iban: profile.iban,
            amount: calc.grossTotal,
            invoiceNumber: meta.invoiceNumber || '—',
          }),
    [
      isEstimate,
      profile.bic,
      kontoinhaber,
      profile.iban,
      calc.grossTotal,
      meta.invoiceNumber,
    ]
  );

  return (
    <div className="mx-auto min-h-[297mm] max-w-[210mm] rounded-sm border border-slate-200 bg-white p-10 text-xs text-slate-900 shadow-[0_25px_60px_-20px_rgba(15,23,42,0.35)]">
      {/* Briefkopf */}
      <div className="mb-10 flex justify-between gap-8 border-b border-slate-200 pb-6">
        <div className="space-y-0.5 text-[11px] leading-relaxed">
          <p className="mb-2 text-[9px] font-bold uppercase tracking-[0.16em] text-slate-500">
            Empfänger
          </p>
          {customer.type === 'private' ? (
            <>
              <p className="text-sm font-semibold text-slate-900">
                {[customer.firstName, customer.lastName].filter(Boolean).join(' ') || '—'}
              </p>
              <p className="text-slate-700">{customer.street || '—'}</p>
              <p className="text-slate-700">
                {[customer.zip, customer.city].filter(Boolean).join(' ') || '—'}
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-slate-900">
                {customer.companyName || '—'}
              </p>
              {customer.contactPerson ? (
                <p className="text-slate-700">{customer.contactPerson}</p>
              ) : null}
              <p className="text-slate-700">{customer.street || '—'}</p>
              <p className="text-slate-700">
                {[customer.zip, customer.city].filter(Boolean).join(' ') || '—'}
              </p>
              {customer.vatId ? (
                <p className="text-slate-600">USt-IdNr.: {customer.vatId}</p>
              ) : null}
            </>
          )}
        </div>
        <div className="max-w-[45%] text-right text-[11px] leading-relaxed">
          <p className="mb-2 text-[9px] font-bold uppercase tracking-[0.16em] text-slate-500">
            Absender
          </p>
          <p className="text-sm font-semibold text-slate-900">{senderName}</p>
          {senderStreet ? <p className="text-slate-700">{senderStreet}</p> : null}
          {senderCityLine ? <p className="text-slate-700">{senderCityLine}</p> : null}
          {profile.taxNumber ? (
            <p className="text-slate-600">Steuer-Nr.: {profile.taxNumber}</p>
          ) : null}
          {profile.vatId ? <p className="text-slate-600">USt-IdNr.: {profile.vatId}</p> : null}
          {sender.email ? <p className="text-slate-600">{sender.email}</p> : null}
          {sender.phone ? <p className="text-slate-600">{sender.phone}</p> : null}
        </div>
      </div>

      {/* Header */}
      <div className="mb-8 border-b-2 border-slate-800 pb-4">
        <p className="mb-1 text-[9px] font-bold uppercase tracking-[0.2em] text-[#b48a56]">
          PreisExpert · PEPapers
        </p>
        <h1 className="mb-4 text-2xl font-black tracking-wide text-slate-900">
          {isEstimate ? 'KOSTENVORANSCHLAG' : 'RECHNUNG'}
        </h1>
        <div className="grid grid-cols-3 gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-[11px]">
          <div>
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
              {isEstimate ? 'KVA-Nr.' : 'Rechnungs-Nr.'}
            </span>
            <p className="mt-0.5 font-semibold text-slate-900">{meta.invoiceNumber || '—'}</p>
          </div>
          <div>
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
              Rechnungsdatum
            </span>
            <p className="mt-0.5 font-semibold text-slate-900">
              {formatDateDe(meta.invoiceDate)}
            </p>
          </div>
          <div>
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
              Leistungsdatum
            </span>
            <p className="mt-0.5 font-semibold text-slate-900">
              {formatDateDe(meta.deliveryDate)}
            </p>
          </div>
        </div>
      </div>

      {/* Positionen */}
      <table className="mb-6 w-full border-collapse text-left text-[11px]">
        <thead>
          <tr className="bg-slate-800 text-[9px] uppercase tracking-wider text-slate-100">
            <th className="px-2 py-2.5 pr-2 font-bold">Pos.</th>
            <th className="py-2.5 pr-2 font-bold">Beschreibung</th>
            <th className="py-2.5 pr-2 text-right font-bold">Menge</th>
            <th className="py-2.5 pr-2 font-bold">Einheit</th>
            <th className="py-2.5 pr-2 text-right font-bold">Einzelpreis</th>
            <th className="py-2.5 pr-2 text-right font-bold">MwSt %</th>
            <th className="py-2.5 pr-2 text-right font-bold">Gesamt</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            const amounts = lineAmounts(
              item.quantity,
              item.unitPrice,
              item.priceMode,
              standardVatRate,
              isVatExempt
            );
            return (
              <tr key={item.id} className="border-b border-slate-200 align-top">
                <td className="px-2 py-2.5 pr-2 text-slate-500">{idx + 1}</td>
                <td className="py-2.5 pr-2">
                  <p className="font-medium text-slate-900">{item.description || '—'}</p>
                  {item.kind === 'service' ? (
                    <p className="mt-0.5 text-[9px] text-slate-500">
                      {formatInvoiceItemServiceMeta(item)}
                    </p>
                  ) : (
                    <p className="mt-0.5 text-[9px] text-slate-500">Verkauf</p>
                  )}
                </td>
                <td className="py-2.5 pr-2 text-right tabular-nums text-slate-800">
                  {item.quantity}
                </td>
                <td className="py-2.5 pr-2 text-slate-700">{item.unit}</td>
                <td className="py-2.5 pr-2 text-right tabular-nums text-slate-800">
                  {formatEuro(item.priceMode === 'net' ? amounts.unitNet : amounts.unitGross)}
                </td>
                <td className="py-2.5 pr-2 text-right tabular-nums text-slate-700">
                  {displayVatRate}%
                </td>
                <td className="py-2.5 pr-2 text-right tabular-nums font-semibold text-slate-900">
                  {formatEuro(amounts.gross)}
                </td>
              </tr>
            );
          })}
          {showShipping ? (
            <tr className="border-b border-slate-200 align-top">
              <td className="px-2 py-2.5 pr-2 text-slate-500">{items.length + 1}</td>
              <td className="py-2.5 pr-2">
                <p className="font-medium text-slate-900">Versand- &amp; Verpackungskosten</p>
              </td>
              <td className="py-2.5 pr-2 text-right tabular-nums text-slate-800">1</td>
              <td className="py-2.5 pr-2 text-slate-700">Pauschal</td>
              <td className="py-2.5 pr-2 text-right tabular-nums text-slate-800">
                {formatEuro(shipping.amount)}
              </td>
              <td className="py-2.5 pr-2 text-right tabular-nums text-slate-700">
                {displayVatRate}%
              </td>
              <td className="py-2.5 pr-2 text-right tabular-nums font-semibold text-slate-900">
                {formatEuro(shipping.amount)}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {/* Summen: Netto (inkl. Versand-Pos.) → USt → Brutto — kein Versand als Extra-Zeile */}
      <div className="mb-8 ml-auto w-full max-w-xs rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1.5 text-[11px]">
        <div className="flex justify-between">
          <span className="text-slate-600">Zwischensumme Netto</span>
          <span className="tabular-nums text-slate-900">{formatEuro(calc.netTotal)}</span>
        </div>
        {isVatExempt ? (
          <p className="text-[10px] text-slate-500">
            Umsatzsteuerbefreit (Stammdaten) – keine Umsatzsteuer ausgewiesen.
          </p>
        ) : (
          <div className="flex justify-between text-slate-600">
            <span>zzgl. USt {standardVatRate} %</span>
            <span className="tabular-nums">
              {formatEuro(calc.vatBreakdown.standard.vat)}
            </span>
          </div>
        )}
        <div className="flex justify-between border-t border-slate-300 pt-2 text-sm font-bold text-slate-900">
          <span>Gesamtbetrag (Brutto)</span>
          <span className="tabular-nums">{formatEuro(calc.grossTotal)}</span>
        </div>
      </div>

      {meta.notes ? (
        <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-3 text-[10px] text-slate-700">
          <p className="mb-1 text-[9px] font-bold uppercase tracking-[0.14em] text-slate-500">
            Anmerkungen
          </p>
          <p className="whitespace-pre-wrap">{meta.notes}</p>
        </div>
      ) : null}

      {/* Zahlung & GiroCode / KVA-Hinweis */}
      {isEstimate ? (
        <div className="mb-8 space-y-2 rounded-lg border border-slate-300 bg-slate-50 p-4 text-[11px] text-slate-700">
          <p>
            Dieses Angebot ist freibleibend und gültig für{' '}
            <strong>{meta.paymentDeadlineDays || 0}</strong> Tage bis zum{' '}
            <strong>{formatDateDe(meta.deliveryDate)}</strong>.
          </p>
          <p className="leading-relaxed text-slate-600">{ESTIMATE_PROGNOSIS_DISCLAIMER}</p>
        </div>
      ) : (
        <div className="mb-8 grid grid-cols-[1fr_auto] gap-5 rounded-lg border border-slate-300 bg-slate-50 p-4">
          <div className="space-y-2 text-[11px]">
            <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-500">
              Zahlungsinformationen
            </p>
            <p className="text-slate-800">
              Zahlbar innerhalb von <strong>{meta.paymentDeadlineDays || 0}</strong> Tagen
              {dueDate ? (
                <>
                  {' '}
                  (bis <strong>{dueDate}</strong>)
                </>
              ) : null}
              .
            </p>
            <div className="space-y-0.5 text-slate-700">
              <p>
                <span className="text-slate-500">Kontoinhaber:</span> {kontoinhaber || '—'}
              </p>
              <p>
                <span className="text-slate-500">Bank:</span> {profile.bankName || '—'}
              </p>
              <p>
                <span className="text-slate-500">IBAN:</span> {profile.iban || '—'}
              </p>
              <p>
                <span className="text-slate-500">BIC:</span> {profile.bic || '—'}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-3 text-center shadow-sm">
            {qrPayload ? (
              <QRCodeSVG value={qrPayload} size={100} level="M" includeMargin={false} />
            ) : (
              <div className="flex h-[100px] w-[100px] items-center justify-center border border-dashed border-slate-300 text-[9px] text-slate-400">
                GiroCode
                <br />
                unvollständig
              </div>
            )}
            <p className="max-w-[130px] text-[9px] font-medium leading-snug text-slate-600">
              Bequem per Banking-App scannen &amp; bezahlen
            </p>
          </div>
        </div>
      )}

      {/* Fußzeile */}
      <div className="border-t border-slate-300 pt-3 text-[9px] text-slate-500">
        <p>
          {[
            profile.accountHolder ? `Rechnungssteller: ${profile.accountHolder}` : null,
            profile.taxNumber ? `Steuernummer: ${profile.taxNumber}` : null,
            profile.vatId ? `USt-IdNr.: ${profile.vatId}` : null,
          ]
            .filter(Boolean)
            .join(' · ') || '—'}
        </p>
      </div>
    </div>
  );
};

export default PEInvoicePreview;
